<template>
  <div class="cards">
    <ul class="breadcrumb">
      <li><router-link to="/">Главная</router-link></li>
      <li class="active"><router-link to="/cards">Мои карты</router-link></li>
    </ul>
    <div class="page_header">
      <div class="page_header--title">
        Мои карты
      </div>
      <div class="page_header--control" v-if="canBindCard && !bindWait.active">
        <button type="button" class="btn" :disabled="binding" @click="bindCard">
          {{ binding ? 'Открываем страницу оплаты...' : 'Привязать карту' }}
        </button>
      </div>
    </div>

    <div class="page_content">
      <div v-if="bindWait.active" class="cards_atmos_wait">
        <span v-if="!bindWait.timedOut" class="cards_atmos_spinner"></span>
        <p v-if="!bindWait.timedOut">Привязка карты открыта в новой вкладке. Ожидаем результат…</p>
        <p v-else>Не удалось подтвердить привязку. Если вы уже привязали карту в открытой вкладке, проверьте ещё раз.</p>
        <button type="button" class="btn" @click="retryBindCheck">Проверить ещё раз</button>
        <button v-if="bindWait.timedOut" type="button" class="cards_atmos_cancel" @click="reopenBindTab">Открыть страницу оплаты ещё раз</button>
        <button type="button" class="cards_atmos_cancel" @click="cancelBindWait">Отменить</button>
      </div>
      <div v-if="pendingBindNotice" class="cards_pending_notice">{{ pendingBindNotice }}</div>
      <cards-list
          :cards="cards"
          :deleting-id="deletingId"
          @delete-card="deleteCard"
      />
    </div>
  </div>
</template>

<script>
import CardsList from "../components/CardsList";
import { atmosRedirectCardBind, createAtmosTabWaiter, openAtmosPopupTab, takePendingAtmosCardBind } from "@/utilities";
import { useToast } from "vue-toastification";

export default {
  name: 'Cards',
  components: { CardsList },
  setup() {
    const toast = useToast();
    return { toast }
  },
  data() {
    return {
      cards: [],
      binding: false,
      deletingId: null,
      pendingBindNotice: '',
      bindWait: { active: false, timedOut: false },
    };
  },
  computed: {
    acquiringIntegrationType() {
      return this.$store.getters.settings?.modules?.payment_types?.online?.acquiring_integration_type || null
    },
    canBindCard() {
      // Пока в ЛК реализован только redirect-флоу привязки карты (ATMOS) — без JS SDK-виджета холда
      // для остальных провайдеров.
      return this.acquiringIntegrationType === 'atmos'
    },
  },
  async created() {
    this.bindWaiter = createAtmosTabWaiter({
      setState: (next) => { this.bindWait = next }
    });
    await this.loadCards();
    await this.handlePendingCardBindReturn();
  },
  methods: {
    async loadCards() {
      try {
        const response = await this.$api.payment.getCards();
        this.cards = response.data || [];
      } catch (error) {
        this.toast.error('Ошибка при загрузке карт');
      }
    },
    async handlePendingCardBindReturn() {
      if (window.opener) {
        return;
      }

      const pending = takePendingAtmosCardBind();
      if (!pending) {
        return;
      }

      // actionCheckCard() не подтверждает привязку ATMOS-карты (см. промт §7.4) — она завершается
      // асинхронным webhook, поэтому просто перечитываем список карт с несколькими попытками, пока
      // не появится карта, которой не было в снимке ДО перехода на страницу оплаты (снимок берётся
      // в bindCard(), а не здесь — к моменту возврата новая карта уже могла попасть и в самый первый
      // loadCards() этого визита, так что сравнивать не с чем без сохранённого снимка).
      const knownCardIds = new Set(pending.knownCardIds || []);
      for (let attempt = 1; attempt <= 5; attempt += 1) {
        if (this.cards.some((card) => !knownCardIds.has(card.id))) {
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 1500));
        await this.loadCards();
      }

      if (!this.cards.some((card) => !knownCardIds.has(card.id))) {
        this.pendingBindNotice = 'Привязка карты обрабатывается. Обновите страницу через несколько секунд.';
      }
    },
    async bindCard() {
      this.pendingBindNotice = '';

      const willOpenAtmosTab = this.acquiringIntegrationType === 'atmos';
      const atmosPopup = willOpenAtmosTab ? openAtmosPopupTab() : null;
      let atmosPopupHandedOff = false;
      this.binding = true;

      try {
        const response = (await this.$api.payment.createCard()).data;
        if (this.acquiringIntegrationType === 'atmos') {
          const knownCardIds = this.cards.map((card) => card.id);
          atmosPopupHandedOff = true;
          const atmosResult = atmosRedirectCardBind(response.invoice_id, {
            paymentLink: response.payment_link,
            knownCardIds,
          }, atmosPopup);

          if (!atmosResult) {
            this.toast.error('Не удалось создать ссылку для привязки карты');
            return;
          }

          if (atmosResult.mode === 'redirect') {
            // Страница ЛК уходит на оплату целиком — вернувшись, created()/handlePendingCardBindReturn
            // подхватит sessionStorage-снимок и опросит карты
            return;
          }

          const knownSet = new Set(knownCardIds);
          const result = await this.bindWaiter.start(
            atmosResult.popup,
            async () => {
              await this.loadCards();
              return this.cards.find((card) => !knownSet.has(card.id)) || false;
            },
            response.payment_link
          );
          if (result) {
            this.toast.success('Карта привязана');
          }
          return;
        }

        this.toast.error('Привязка карты для этого провайдера пока не поддерживается в личном кабинете');
      } catch (error) {
        this.toast.error(error?.response?.data?.message || 'Не удалось создать ссылку для привязки карты');
      } finally {
        if (atmosPopup && !atmosPopupHandedOff) {
          try {
            atmosPopup.close();
          } catch (e) {
            console.log('failed to close unused atmos tab', e);
          }
        }
        this.binding = false;
      }
    },
    async retryBindCheck() {
      const result = await this.bindWaiter.retry();
      if (result) {
        this.toast.success('Карта привязана');
      }
    },
    cancelBindWait() {
      this.bindWaiter.cancel();
      this.pendingBindNotice = '';
    },
    reopenBindTab() {
      this.bindWaiter.reopen();
    },
    async deleteCard(id) {
      this.deletingId = id;
      try {
        await this.$api.payment.deleteCard(id);
        this.cards = this.cards.filter((card) => card.id !== id);
        this.toast.success('Карта удалена');
      } catch (error) {
        if (error?.response?.status === 409) {
          this.toast.error(error?.response?.data?.message || 'Карта привязана к активной подписке');
        } else {
          this.toast.error(error?.response?.data?.message || 'Не удалось удалить карту');
        }
      } finally {
        this.deletingId = null;
      }
    },
  },
  unmounted() {
    this.bindWaiter.cancel();
  },
}
</script>
